import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CalendarPlus, ClipboardList, CreditCard, MessageSquare, Home, Settings2, LogOut } from "lucide-react";
import portalApi from "../../lib/portalApi";
import { OverviewTab, BookTab } from "../../components/portal/BookingTabs";
import { FormsTab } from "../../components/portal/FormsTab";
import { PayTab, MessagesTab, PrefsTab } from "../../components/portal/AccountTabs";

const TABS = [
  { key: "home", label: "Home", icon: Home },
  { key: "book", label: "Book", icon: CalendarPlus },
  { key: "forms", label: "Forms", icon: ClipboardList },
  { key: "pay", label: "Pay", icon: CreditCard },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "prefs", label: "Settings", icon: Settings2 },
];

export default function PortalHome() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "home";
  const [me, setMe] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("cw_portal_token")) { navigate("/portal/login"); return; }
    portalApi.get("/portal/me").then(({ data }) => setMe(data)).catch(() => {});
  }, [navigate]);

  const go = (key) => setParams(key === "home" ? {} : { tab: key });
  const logout = () => { localStorage.removeItem("cw_portal_token"); navigate("/portal/login"); };
  if (!me) return <div className="h-screen flex items-center justify-center"><div className="mf-skeleton w-40 h-10" /></div>;

  const props = { me, setMe, go };
  return (
    <div className="min-h-screen pb-24 md:pb-10" style={{ background: "var(--mf-bg)" }} data-testid="portal-home">
      <header className="cw-safe-top sticky top-0 z-20 px-4 md:px-8 py-3 flex items-center justify-between" style={{ background: "color-mix(in srgb, var(--mf-bg) 88%, transparent)", backdropFilter: "blur(16px)" }}>
        <div className="flex items-center gap-2 min-w-0">
          <img src="/icons/icon-192.png" alt="" className="w-8 h-8 rounded-xl" />
          <div className="min-w-0">
            <div className="font-serif text-lg leading-tight truncate" style={{ color: "var(--mf-primary)" }}>{me.practice_name || "Clinician Well"}</div>
            <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Client Portal</div>
          </div>
        </div>
        <button onClick={logout} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-full" style={{ background: "var(--mf-surface)" }} data-testid="portal-logout"><LogOut className="w-4 h-4" /> Sign out</button>
      </header>
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <nav className="hidden md:flex gap-2 my-6" data-testid="portal-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => go(t.key)} className="px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors"
              style={{ background: tab === t.key ? "var(--mf-primary)" : "var(--mf-surface)", color: tab === t.key ? "#FAFAF8" : "var(--mf-text)" }} data-testid={`portal-tab-${t.key}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="pt-4 md:pt-0">
          {tab === "home" && <OverviewTab {...props} />}
          {tab === "book" && <BookTab {...props} />}
          {tab === "forms" && <FormsTab {...props} />}
          {tab === "pay" && <PayTab {...props} />}
          {tab === "messages" && <MessagesTab {...props} />}
          {tab === "prefs" && <PrefsTab {...props} />}
        </div>
      </div>
      <nav className="cw-bottom-nav md:hidden fixed bottom-0 left-0 right-0 flex justify-around pt-2 z-30" style={{ background: "var(--mf-surface)", borderTop: "1px solid var(--mf-muted)" }} data-testid="portal-mobile-nav">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => go(t.key)} className="flex flex-col items-center py-1 px-1 min-w-[48px]" style={{ color: tab === t.key ? "var(--mf-primary)" : "var(--mf-text-muted)" }} data-testid={`portal-mtab-${t.key}`}>
            <t.icon className="w-5 h-5" strokeWidth={1.75} />
            <span className="text-[10px] mt-0.5">{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
